(() => {
  const COMPONENT_NAME = "shop-by-colour-pattern";

  if (customElements.get(COMPONENT_NAME)) return;

  const FADE_MS = 220;
  const SWIPER_POLL_MS = 50;
  const SWIPER_WAIT_MS = 10000;

  let swiperReadyPromise = null;

  const whenSwiperReady = (signal) => {
    if (typeof window.Swiper !== "undefined") {
      return Promise.resolve();
    }

    if (!swiperReadyPromise) {
      swiperReadyPromise = new Promise((resolve, reject) => {
        let settled = false;

        const finish = (error) => {
          if (settled) return;
          settled = true;
          window.clearInterval(pollId);
          window.clearTimeout(timeoutId);
          swiperReadyPromise = null;

          if (error) {
            reject(error);
            return;
          }

          resolve();
        };

        const pollId = window.setInterval(() => {
          if (typeof window.Swiper !== "undefined") {
            finish();
          }
        }, SWIPER_POLL_MS);

        const timeoutId = window.setTimeout(() => {
          finish(new Error("Swiper load timeout"));
        }, SWIPER_WAIT_MS);

        const script = document.querySelector('script[src*="swiper-bundle"]');

        if (script) {
          script.addEventListener(
            "load",
            () => {
              if (typeof window.Swiper !== "undefined") {
                finish();
              }
            },
            { once: true },
          );

          script.addEventListener(
            "error",
            () => {
              finish(new Error("Swiper failed to load"));
            },
            { once: true },
          );
        }
      });
    }

    if (!signal) {
      return swiperReadyPromise;
    }

    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }

      const onAbort = () => {
        reject(new DOMException("Aborted", "AbortError"));
      };

      signal.addEventListener("abort", onAbort, { once: true });

      swiperReadyPromise.then(
        (value) => {
          signal.removeEventListener("abort", onAbort);
          resolve(value);
        },
        (error) => {
          signal.removeEventListener("abort", onAbort);
          reject(error);
        },
      );
    });
  };

  class ShopByColourPattern extends HTMLElement {
    #abortController = null;
    #panels = [];
    #swatches = [];
    #ctas = [];
    #activePanelId = null;
    #isTransitioning = false;
    #swiper = null;
    #swiperInitPromise = null;
    #mobileLayout = "grid";
    #panelsMinHeight = 0;
    #rootMinHeight = 0;

    connectedCallback() {
      this.#abortController = new AbortController();
      this.#mobileLayout = this.dataset.mobileLayout || "grid";

      this.#relocateChrome();
      this.#collectElements();
      this.#bindSwatches();
      this.#upgradeSemantics();

      const initialSwatch = this.#swatches.find(
        (swatch) => swatch.getAttribute("aria-pressed") === "true",
      );
      const initialPanel = this.#panels.find((panel) => !panel.hidden);
      const fallbackIndex = Math.floor((this.#swatches.length - 1) / 2);
      const initialId =
        initialSwatch?.dataset.shopByColourSwatch ||
        initialPanel?.dataset.shopByColourPanel ||
        this.#swatches[fallbackIndex]?.dataset.shopByColourSwatch ||
        this.#panels[fallbackIndex]?.dataset.shopByColourPanel;

      if (initialId) {
        this.#syncActiveChrome(initialId, { immediate: true });
      }

      this.#setupMobileCarousel();

      window.addEventListener("resize", () => this.#resetPanelsHeight(), {
        signal: this.#abortController.signal,
      });

      this.classList.add("shop-by-colour--enhanced");
    }

    disconnectedCallback() {
      this.#destroySwiper();
      this.#abortController?.abort();
      this.#abortController = null;
      this.#panels = [];
      this.#swatches = [];
      this.#ctas = [];
      this.#activePanelId = null;
      this.#isTransitioning = false;
      this.#panelsMinHeight = 0;
      this.#rootMinHeight = 0;
      this.classList.remove("shop-by-colour--enhanced");
    }

    #collectElements() {
      this.#panels = [...this.querySelectorAll("[data-shop-by-colour-panel]")];
      this.#swatches = [
        ...this.querySelectorAll("[data-shop-by-colour-swatch]"),
      ];
      this.#ctas = [...this.querySelectorAll("[data-shop-by-colour-cta]")];
    }

    #relocateChrome() {
      const swatchesContainer = this.querySelector(
        "[data-shop-by-colour-swatches]",
      );
      const ctasContainer = this.querySelector("[data-shop-by-colour-ctas]");
      const panelsContainer = this.querySelector(
        "[data-shop-by-colour-panels]",
      );

      if (!swatchesContainer || !panelsContainer) return;

      for (const swatch of panelsContainer.querySelectorAll(
        "[data-shop-by-colour-swatch]",
      )) {
        swatchesContainer.appendChild(swatch);
      }

      if (ctasContainer) {
        for (const cta of panelsContainer.querySelectorAll(
          "[data-shop-by-colour-cta]",
        )) {
          ctasContainer.appendChild(cta);
        }
      }
    }

    #bindSwatches() {
      const controller = this.#abortController;
      if (!controller) return;

      const { signal } = controller;

      for (const swatch of this.#swatches) {
        swatch.addEventListener(
          "click",
          () => {
            this.#activatePanel(swatch.dataset.shopByColourSwatch);
          },
          { signal },
        );

        swatch.addEventListener(
          "keydown",
          (event) => {
            this.#handleSwatchKeydown(event, swatch);
          },
          { signal },
        );
      }
    }

    #handleSwatchKeydown(event, swatch) {
      const key = event.key;
      if (
        key !== "ArrowRight" &&
        key !== "ArrowLeft" &&
        key !== "Home" &&
        key !== "End"
      ) {
        return;
      }

      event.preventDefault();

      if (this.#isTransitioning) return;

      const currentIndex = this.#swatches.indexOf(swatch);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;

      if (key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % this.#swatches.length;
      } else if (key === "ArrowLeft") {
        nextIndex =
          (currentIndex - 1 + this.#swatches.length) % this.#swatches.length;
      } else if (key === "Home") {
        nextIndex = 0;
      } else if (key === "End") {
        nextIndex = this.#swatches.length - 1;
      }

      const nextSwatch = this.#swatches[nextIndex];
      const nextId = nextSwatch?.dataset.shopByColourSwatch;

      if (!nextId || nextId === this.#activePanelId) return;

      nextSwatch.focus();
      this.#activatePanel(nextId);
    }

    #upgradeSemantics() {
      const swatchlist = this.querySelector("[data-shop-by-colour-swatches]");

      if (swatchlist) {
        swatchlist.setAttribute("role", "tablist");

        if (swatchlist.dataset.swatchesLabel) {
          swatchlist.setAttribute(
            "aria-label",
            swatchlist.dataset.swatchesLabel,
          );
        }
      }

      for (const swatch of this.#swatches) {
        const panelId = swatch.dataset.shopByColourSwatch;
        swatch.setAttribute("role", "tab");
        swatch.setAttribute("aria-controls", `ShopByColourPanel-${panelId}`);
        swatch.setAttribute("aria-selected", "false");
        swatch.tabIndex = -1;
      }

      for (const panel of this.#panels) {
        const panelId = panel.dataset.shopByColourPanel;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", `ShopByColourSwatch-${panelId}`);
      }
    }

    async #activatePanel(panelId) {
      if (
        !panelId ||
        panelId === this.#activePanelId ||
        this.#isTransitioning
      ) {
        return;
      }

      await this.#syncActiveChrome(panelId, { immediate: false });
    }

    async #syncActiveChrome(panelId, { immediate = false } = {}) {
      const nextPanel = this.#getPanelById(panelId);
      if (!nextPanel) return;

      const currentPanel = this.#getPanelById(this.#activePanelId);
      const animate = !immediate && currentPanel && currentPanel !== nextPanel;

      if (animate) {
        this.#isTransitioning = true;
        this.#holdPanelsHeight();
        currentPanel.classList.add("is-fading-out");
        this.classList.add("shop-by-colour--fading");
        await this.#wait(FADE_MS);
        currentPanel.classList.remove("is-fading-out");
        currentPanel.hidden = true;
      }

      this.#applyBackgrounds(panelId);

      for (const panel of this.#panels) {
        const isActive = panel.dataset.shopByColourPanel === panelId;
        panel.hidden = !isActive;
        panel.classList.remove("is-fading-out", "is-fading-in", "is-visible");
      }

      for (const swatch of this.#swatches) {
        const isActive = swatch.dataset.shopByColourSwatch === panelId;
        swatch.setAttribute("aria-selected", isActive ? "true" : "false");
        swatch.setAttribute("aria-pressed", isActive ? "true" : "false");
        swatch.tabIndex = isActive ? 0 : -1;
      }

      for (const cta of this.#ctas) {
        cta.hidden = cta.dataset.shopByColourCta !== panelId;
      }

      this.#activePanelId = panelId;

      // Laid out but still transparent, so Swiper can measure the new panel
      // before it is ever seen — otherwise it flashes as a grid first.
      if (animate) {
        nextPanel.classList.add("is-fading-in");
      }

      await this.#refreshMobileCarousel();

      if (animate) {
        await this.#wait(16);
        nextPanel.classList.add("is-visible");
        await this.#wait(FADE_MS);
        nextPanel.classList.remove("is-fading-in", "is-visible");
        this.classList.remove("shop-by-colour--fading");
        this.#isTransitioning = false;
      }

      this.#holdPanelsHeight();
    }

    /* Panels are swapped one at a time, so the products area would collapse and
       regrow on every change. Hold the tallest height seen — on the section as
       well as the products, since the CTA and swatches shift too — and only let
       it go when the viewport changes. */
    #holdPanelsHeight() {
      const panelsHost = this.querySelector("[data-shop-by-colour-panels]");

      if (panelsHost) {
        this.#panelsMinHeight = Math.max(
          this.#panelsMinHeight,
          panelsHost.offsetHeight,
        );

        if (this.#panelsMinHeight > 0) {
          panelsHost.style.minHeight = `${this.#panelsMinHeight}px`;
        }
      }

      this.#rootMinHeight = Math.max(this.#rootMinHeight, this.offsetHeight);

      if (this.#rootMinHeight > 0) {
        this.style.minHeight = `${this.#rootMinHeight}px`;
      }
    }

    #resetPanelsHeight() {
      if (this.#isTransitioning) return;

      const panelsHost = this.querySelector("[data-shop-by-colour-panels]");
      this.#panelsMinHeight = 0;
      this.#rootMinHeight = 0;
      this.style.minHeight = "";

      if (panelsHost) {
        panelsHost.style.minHeight = "";
      }
    }

    #applyBackgrounds(panelId) {
      const swatch = this.#swatches.find(
        (item) => item.dataset.shopByColourSwatch === panelId,
      );

      if (!swatch) return;

      const contentBg = swatch.dataset.contentBg;
      const productBg = swatch.dataset.productBg;

      if (contentBg) {
        this.style.setProperty("--shop-by-colour-content-bg", contentBg);
      } else {
        this.style.removeProperty("--shop-by-colour-content-bg");
      }

      if (productBg) {
        this.style.setProperty("--shop-by-colour-product-bg", productBg);
      } else {
        this.style.removeProperty("--shop-by-colour-product-bg");
      }
    }

    #getPanelById(panelId) {
      return this.#panels.find(
        (panel) => panel.dataset.shopByColourPanel === panelId,
      );
    }

    #setupMobileCarousel() {
      if (this.#mobileLayout !== "carousel") return;

      const mediaQuery = window.matchMedia("(max-width: 767px)");
      const controller = this.#abortController;
      if (!controller) return;

      const sync = () => {
        if (mediaQuery.matches) {
          this.#initSwiper();
        } else {
          this.#destroySwiper();
        }
      };

      mediaQuery.addEventListener("change", sync, {
        signal: controller.signal,
      });
      sync();
    }

    async #refreshMobileCarousel() {
      if (this.#mobileLayout !== "carousel") return;
      if (!window.matchMedia("(max-width: 767px)").matches) return;

      // Leave the old bullets on screen across the swap. Clearing them drops
      // the pagination out of flow and the section jumps by its height.
      this.#destroySwiper({ keepPagination: true });
      await this.#initSwiper();
    }

    async #initSwiper() {
      if (this.#swiper) return;
      if (this.#swiperInitPromise) return this.#swiperInitPromise;

      this.#swiperInitPromise = this.#mountSwiper();

      try {
        await this.#swiperInitPromise;
      } finally {
        this.#swiperInitPromise = null;
      }

      if (!this.#swiper) {
        this.#clearPagination();
      }
    }

    async #mountSwiper() {
      try {
        await whenSwiperReady(this.#abortController?.signal);
      } catch {
        return;
      }

      if (!this.isConnected || typeof window.Swiper === "undefined") return;
      if (this.#swiper) return;
      if (!window.matchMedia("(max-width: 767px)").matches) return;

      const activePanel = this.#getPanelById(this.#activePanelId);
      if (!activePanel) return;

      const grid = activePanel.querySelector(".shop-by-colour-panel__grid");
      if (!grid || grid.children.length === 0) return;

      grid.classList.add("swiper-wrapper");

      for (const child of grid.children) {
        child.classList.add("swiper-slide");
      }

      activePanel.classList.add("swiper");

      const paginationEl = this.querySelector(
        "[data-shop-by-colour-pagination]",
      );
      const slideLabelTemplate = paginationEl?.dataset.slideLabelTemplate ?? "";

      this.#swiper = new window.Swiper(activePanel, {
        slidesPerView: "auto",
        spaceBetween: this.#getCarouselGap(),
        pagination: paginationEl
          ? {
              el: paginationEl,
              clickable: true,
              bulletClass: "carousel-pagination__bullet",
              bulletActiveClass: "carousel-pagination__bullet--active",
              renderBullet: (index, className) => {
                const label = slideLabelTemplate.replace(
                  "__SLIDE__",
                  String(index + 1),
                );

                return `<button type="button" class="${className}" aria-label="${label.replaceAll('"', "&quot;")}"></button>`;
              },
            }
          : undefined,
      });

      if (paginationEl) {
        paginationEl.hidden = false;
      }
    }

    #destroySwiper({ keepPagination = false } = {}) {
      if (this.#swiper) {
        const el = this.#swiper.el;
        this.#swiper.destroy(true, true);
        this.#swiper = null;

        if (el) {
          el.classList.remove("swiper");
          const grid = el.querySelector(".shop-by-colour-panel__grid");
          grid?.classList.remove("swiper-wrapper");

          for (const slide of el.querySelectorAll(".swiper-slide")) {
            slide.classList.remove("swiper-slide");
          }
        }
      }

      if (!keepPagination) {
        this.#clearPagination();
      }
    }

    #clearPagination() {
      const paginationEl = this.querySelector(
        "[data-shop-by-colour-pagination]",
      );

      if (!paginationEl) return;

      paginationEl.hidden = true;
      paginationEl.innerHTML = "";
    }

    #getCarouselGap() {
      const parsed = Number.parseInt(this.dataset.carouselGap || "", 10);

      return Number.isNaN(parsed) ? 0 : parsed;
    }

    #wait(ms) {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    }
  }

  customElements.define(COMPONENT_NAME, ShopByColourPattern);
})();
